import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn';

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-lg border border-gray-200 bg-white shadow-sm', className)}>
      {children}
    </div>
  );
}

/**
 * A section header. Pass `icon` + `description` to give a subsection its own
 * identity glyph and a one-line explanation of what it shows (house pattern:
 * every distinct component announces itself). `aside` stays right-aligned.
 *
 * The icon renders INLINE with the title and the description spans the full
 * width, so every text edge in the card (title block, description, body
 * content) shares the same px-4 left rail. No tile indent — an indented
 * header next to a flush body is exactly the inconsistency this prevents.
 */
export function CardHeader({
  title,
  description,
  icon: Icon,
  aside,
  meta,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  aside?: ReactNode;
  /**
   * An optional metadata row UNDER the description: capture time, a refresh
   * control, provenance. Left-aligned on the same px-4 rail as everything else,
   * so a header can carry "when was this measured / refresh it" without
   * stacking it into `aside` and crowding the title row's right edge.
   * Additive and optional; headers that don't pass it render exactly as before.
   */
  meta?: ReactNode;
}) {
  return (
    <div className="px-4 pb-1.5 pt-3.5">
      <div className="flex items-start justify-between gap-3">
        <h3 className="flex min-w-0 items-center gap-2 text-base font-semibold text-gray-900">
          {Icon && <Icon className="h-4 w-4 flex-shrink-0 text-blue-600" />}
          <span className="min-w-0">{title}</span>
        </h3>
        {aside && <div className="flex-shrink-0">{aside}</div>}
      </div>
      {/* Description first: it says what the card IS, which has to land before
          any provenance about it. The meta row sits under it as a footnote. */}
      {description && <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{description}</p>}
      {meta && <div className="mt-2">{meta}</div>}
    </div>
  );
}

export function CardBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('p-4', className)}>{children}</div>;
}
